// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
} from 'firebase/analytics';
import { logFirebaseError } from './errorLog';
import { storageBucket } from './consts';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyBMbNQ8Tzi-eYgtJ8Q6tix2jbZi3qFqT9s',
  authDomain: 'muva-sport.firebaseapp.com',
  projectId: 'muva-sport',
  storageBucket,
  messagingSenderId: '115484975750',
  appId: '1:115484975750:web:ae01ed0361bceb0b78a707',
  measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID,
};

// The Cloud project's number, which the Google Picker takes as its app id
export const projectNumber = firebaseConfig.messagingSenderId;

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
// IndexedDB-backed cache: on a repeat visit onSnapshot fires from the local
// cache first and catches up over the network, so the app is not gated on a
// round-trip. Multi-tab manager keeps it working with several tabs open.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
export const storage = getStorage(app);

// Google Analytics 4. Resolves to null where the browser cannot support it
// (no cookies, no IndexedDB) or when no measurement id is configured, so
// every caller must tolerate a null instance.
export const analyticsPromise = (async () => {
  if (!firebaseConfig.measurementId) return null;
  if (!(await isAnalyticsSupported())) return null;
  return getAnalytics(app);
})();
const functions = getFunctions(app);

// Firebase Cloud Messaging — guarded: unsupported browsers (Firefox private,
// iOS non-PWA, old browsers) throw on getMessaging().
export const getMessagingInstance = async () => {
  if (!(await isSupported())) return null;
  return getMessaging(app);
};

// Firebase cloud functions
//
// Wrapped rather than exported raw so that every rejection is described in the
// console — a callable refused by the rules otherwise reaches the caller as a
// bare "Missing or insufficient permissions." with no hint of which function
// was called. The error is rethrown unchanged, so the existing per-caller
// try/catch + toast keeps working.
const callable = (name, httpsOptions) => {
  const fn = httpsCallable(functions, name, httpsOptions);

  return async (data, options) => {
    try {
      return await fn(data, options);
    } catch (error) {
      logFirebaseError(error, {
        // Field names only, never their values: these payloads carry invite
        // tokens, e-mail addresses, personal data and base64 signature blobs,
        // and the console is not the place for any of it.
        operation: `callable ${name}`,
        extra: {
          payloadFields:
            data && typeof data === 'object' ? Object.keys(data) : typeof data,
        },
      });
      throw error;
    }
  };
};

export const teamUpdate = callable('team-update');
export const teamCreate = callable('team-create');
export const tournamentUpdate = callable('tournament-update');
export const tournamentInfoSet = callable('tournament-infoSet');
export const tournamentCreate = callable('tournament-create');
export const tournamentTeamUpdate = callable('tournament-teamUpdate');
export const tournamentFieldCreate = callable('tournament-fieldCreate');
export const tournamentFieldUpdate = callable('tournament-fieldUpdate');
export const tournamentFieldDelete = callable('tournament-fieldDelete');
export const tournamentGamesCreate = callable('tournament-gamesCreate');
export const tournamentGameUpdate = callable('tournament-gameUpdate');
export const tournamentGameRefereesSet = callable('tournament-gameRefereesSet');
export const tournamentGamesDelete = callable('tournament-gamesDelete');
export const tournamentSetCheckIn = callable('tournament-setCheckIn');
export const tournamentRefereeRateSet = callable('tournament-refereeRateSet');
export const tournamentRefereesInvite = callable('tournament-refereesInvite');
export const tournamentRefereeRespond = callable('tournament-refereeRespond');
export const tournamentRefereeRevoke = callable('tournament-refereeRevoke');
export const organizationUpdateProfile = callable('organization-updateProfile');
export const tournamentRefereeGameRateSet = callable(
  'tournament-refereeGameRateSet',
);
// A hundred games take the model about two minutes; the client's default
// timeout gives up at 70 s, the function itself at 540
export const tournamentScheduleImportParse = callable(
  'tournament-scheduleImportParse',
  { timeout: 540000 },
);
export const tournamentScheduleImportApply = callable(
  'tournament-scheduleImportApply',
);
export const tournamentScheduleSourceCheck = callable(
  'tournament-scheduleSourceCheck',
);
export const googleConnect = callable('google-connect');
export const googleStatus = callable('google-status');
export const googleDisconnect = callable('google-disconnect');
export const userUpdate = callable('user-update');
export const userCreate = callable('user-create');
export const userRemoveTeam = callable('user-removeTeam');
export const userUseToken = callable('user-useToken');
export const userSetTourStatus = callable('user-setTourStatus');
export const userSetParentStatus = callable('user-setParentStatus');
export const userSetOrganization = callable('user-setOrganization');
export const tokenCreate = callable('token-create');
export const tokenGet = callable('token-get');
export const authCheckAuthByEmail = callable('auth-checkAuthByEmail');
export const authSendSignInLinkToEmail = callable('auth-sendSignInLinkToEmail');
export const authSendEmailVerification = callable('auth-sendEmailVerification');
export const authSendPasswordResetEmail = callable(
  'auth-sendPasswordResetEmail',
);
export const squareCreatePaymentLink = callable('square-createPaymentLink');
export const paymentCreateManual = callable('payment-createManual');
export const payoutPay = callable('payout-pay');
export const documentVerify = callable('document-verify');
export const waiverGetUnsigned = callable('waiver-getUnsigned');
export const waiverGetTemplate = callable('waiver-getTemplate');
export const waiverSign = callable('waiver-sign');
export const chatSend = callable('chat-send');
export const chatMarkRead = callable('chat-markRead');
export const assistantDictationUrl = callable('assistant-dictationUrl');
export const pushRegisterToken = callable('push-registerToken');
export const pushDeleteToken = callable('push-deleteToken');

// Firebase utils
export const isEmailLink = () =>
  isSignInWithEmailLink(auth, window.location.href);

export const signInByLink = async (email) =>
  await signInWithEmailLink(auth, email, window.location.href);
